import { color } from "@rneui/base";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { StyleSheet, View } from "react-native";
import { Card, useTheme } from "react-native-elements";
import { PlayerContext } from "../backend/PlayerContext";
import {
  allocateResearchersToSchool,
  calculateResearcherCap,
  getBaseResearchRate,
  getFreeResearchers,
} from "../backend/research/Researchers";
import {
  School,
  getSchoolLevel,
  getUnlockedSchools,
} from "../backend/spells/ElementsAndSchools";
import { formatNumber } from "../utils/FormattingUtils";
import ActionsContainer from "./utility/ActionsContainer";
import { Card as UtilityCard } from "./utility/Card";
import { CheckBox } from "./utility/CheckBox";
import { PlusMinusAllocator } from "./utility/PlusMinusAllocator";
import { Text } from "./utility/Text";
import { UIContext, useUIStateFromContext } from "./utility/UIContext";

function ResearchRow(props: {
  school: School;
  applyAll: boolean;
  isOdd: boolean;
}) {
  const school = props.school;
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const freeResearchers = getFreeResearchers(playerContext);
  const hasFreeResearchers = freeResearchers > 0;

  const current =
    playerContext.research.researcherAllocation.schools?.[school] || 0;
  const onPressMinus = useCallback(
    () =>
      playerContextRef.current.apply(
        allocateResearchersToSchool(school, props.applyAll ? -Infinity : -1),
      ),
    [school, props.applyAll],
  );
  const onPressPlus = useCallback(
    () =>
      playerContextRef.current.apply(
        allocateResearchersToSchool(school, props.applyAll ? Infinity : 1),
      ),
    [school, props.applyAll],
  );
  const schoolLevel = getSchoolLevel(playerContext, school);

  const theme = useTheme();

  const schoolText = useMemo(
    () => (
      <Text numberOfLines={3}>
        {school}
        {playerContext.primarySchool == school ? " (Primary)" : ""}:{" "}
        {"Lv" + schoolLevel}
      </Text>
    ),
    [school, playerContext.primarySchool, schoolLevel],
  );
  return useMemo(
    () => (
      <View
        key={school}
        style={[
          styles.row,
          styles.singleSchoolElementContainer,
          props.isOdd && {
            backgroundColor: color(theme.theme.colors.grey5).alpha(0.5),
          },
        ]}
      >
        <View style={styles.schoolTextContainer}>{schoolText}</View>
        <View style={[styles.row, styles.rightHandSide]}>
          <Text>{current}</Text>
          <View style={styles.plusMinusAllocatorContainer}>
            <PlusMinusAllocator
              disableMinus={current === 0}
              disablePlus={!hasFreeResearchers}
              onPressMinus={onPressMinus}
              onPressPlus={onPressPlus}
            />
          </View>
        </View>
      </View>
    ),
    [
      schoolText,
      current,
      hasFreeResearchers,
      onPressMinus,
      onPressPlus,
      theme,
      props.isOdd,
    ],
  );
}

export function Research() {
  const playerContext = useContext(PlayerContext);
  const uiContext = useContext(UIContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const freeResearchers = getFreeResearchers(playerContext);

  const [applyAll, setApplyAll] = useUIStateFromContext(
    uiContext,
    "research_applyall",
    false,
  );

  const toggleApplyAll = useCallback(() => {
    setApplyAll(!applyAll);
  }, [applyAll, setApplyAll]);

  const bottomSection = useMemo(
    () => (
      <View>
        <View style={styles.assignAllContainer}>
          <CheckBox
            title="Assign / unassign all at once"
            checked={applyAll}
            textStyle={styles.checkboxText}
            containerStyle={styles.checkboxContainer}
            onPress={toggleApplyAll}
          />
        </View>
        <View style={[styles.row, styles.researcherCapacityContainer]}>
          <Text>Researchers available: </Text>
          <Text>
            {formatNumber(freeResearchers)}/
            {formatNumber(playerContext.research.totalResearchers)}
          </Text>
        </View>
        <View style={[styles.row, styles.researcherCapacityContainer]}>
          <Text>Researcher capacity: </Text>
          <Text>
            {formatNumber(playerContext.research.totalResearchers)}/
            {formatNumber(calculateResearcherCap(playerContext))}
          </Text>
        </View>
        <View style={[styles.row, styles.researcherCapacityContainer]}>
          <Text>Exp per researcher: </Text>
          <Text>
            {formatNumber(getBaseResearchRate(playerContext), {
              showDecimals: true,
            })}{" "}
            / sec
          </Text>
        </View>
        <View>
          <ActionsContainer area="Research" />
        </View>
      </View>
    ),
    [
      calculateResearcherCap(playerContext),
      getBaseResearchRate(playerContext),
      playerContext.research.totalResearchers,
      freeResearchers,
      applyAll,
      toggleApplyAll,
    ],
  );
  const schools = getUnlockedSchools(playerContext);

  const disableAllMinus =
    freeResearchers === playerContext.research.totalResearchers;
  const disableAllPlus = freeResearchers <= 0;
  const onPressMinusAll = useCallback(() => {
    playerContextRef.current.apply((state) => {
      for (let school in state.research.researcherAllocation.schools) {
        allocateResearchersToSchool(
          school as School,
          applyAll ? -Infinity : -1,
        )(state);
      }
      return state;
    });
  }, [applyAll]);
  const onPressPlusAll = useCallback(() => {
    playerContextRef.current.apply((state) => {
      do {
        for (let school of getUnlockedSchools(state)) {
          allocateResearchersToSchool(school as School, 1)(state);
        }
      } while (applyAll && getFreeResearchers(state) > 0);
      return state;
    });
  }, [applyAll]);

  const theme = useTheme();

  return useMemo(() => {
    return (
      <View>
        <UtilityCard>
          <Card.Title>Schools</Card.Title>
          {schools.map((school, index) => {
            return (
              <ResearchRow
                key={school}
                school={school}
                applyAll={applyAll}
                isOdd={index % 2 != 0}
              />
            );
          })}
          <View
            key="All"
            style={[
              styles.row,
              styles.singleSchoolElementContainer,
              schools.length % 2 != 0 && {
                backgroundColor: color(theme.theme.colors.grey5).alpha(0.5),
              },
            ]}
          >
            <View style={styles.schoolTextContainer}>
              <Text numberOfLines={3}>Assign to every School</Text>
            </View>
            <View style={[styles.row, styles.rightHandSide]}>
              <View style={styles.plusMinusAllocatorContainer}>
                <PlusMinusAllocator
                  disableMinus={disableAllMinus}
                  disablePlus={disableAllPlus}
                  onPressMinus={onPressMinusAll}
                  onPressPlus={onPressPlusAll}
                />
              </View>
            </View>
          </View>
        </UtilityCard>
        {bottomSection}
      </View>
    );
  }, [
    schools.length,
    bottomSection,
    applyAll,
    disableAllMinus,
    disableAllPlus,
    onPressMinusAll,
    onPressPlusAll,
    theme,
  ]);
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  plusMinusAllocatorContainer: {
    marginLeft: 16,
  },
  assignAllContainer: {
    marginTop: 8,
    paddingHorizontal: 16,
  },
  researcherCapacityContainer: {
    marginTop: 4,
    paddingHorizontal: 16,
  },
  singleSchoolElementContainer: {
    padding: 4,
  },
  checkboxContainer: {
    marginLeft: 0,
    marginTop: 8,
    padding: 0,
  },
  checkboxText: {
    fontWeight: "normal",
  },
  rightHandSide: {
    marginLeft: 16,
  },
  schoolTextContainer: {
    flex: 1,
  },
});
